import React, { useState } from 'react';
import { X, Zap, ShieldCheck, Gift, LayoutDashboard, BookMarked } from 'lucide-react';

const TOOLS = {
  ev: {
    key: 'ev',
    icon: Zap,
    title: '+EV Finder',
    tagline: 'Beat the books long-term',
    brief:
      'This tool surfaces bets where the odds being offered are mathematically in your favor — known as positive expected value (+EV). Sportsbooks occasionally misprice a line relative to the true probability of an outcome, creating a window where the bet favors you. Place enough of these consistently and the edge shows up in your results over time.',
    learn: [
      {
        heading: 'What is +EV?',
        body: "Every bet has an implied probability — the chance of winning that the sportsbook's odds suggest. A +EV bet is one where the true probability of that outcome is higher than the implied probability. In other words, the book is offering better odds than the outcome actually deserves. Over a large enough sample, these bets produce a profit.",
      },
      {
        heading: 'How we calculate it',
        body: "We use the sharpest available market as a reference for the true probability of each outcome. Sharp markets operate with very thin margins and are treated as the most accurate pricing available. When a mainstream sportsbook's odds diverge meaningfully from that reference — in your favor — it gets flagged as a +EV opportunity.",
      },
      {
        heading: 'Reading the results',
        body: "Each opportunity shows an EV% — the expected return per dollar wagered over time. A 5% EV means you'd expect to profit $5 for every $100 bet on average. The Kelly stake shows a suggested bet size based on your bankroll setting. The Kelly fraction in your account settings controls how aggressive the sizing is.",
      },
      {
        heading: 'Account health',
        body: "Sportsbooks monitor for sharp betting patterns and may limit or restrict accounts that consistently take +EV lines. Spread your action across different books, avoid betting maximum amounts on every opportunity, and don't focus exclusively on a single sportsbook. Protecting your account access is what lets you keep finding edges.",
      },
    ],
  },

  arbitrage: {
    key: 'arb',
    icon: ShieldCheck,
    title: 'Arbitrage Finder',
    tagline: 'Guaranteed profit, no risk',
    brief:
      'Arbitrage occurs when sportsbooks price the same game far enough apart that betting both sides guarantees a profit regardless of the outcome. This tool finds those gaps and calculates exactly how much to stake on each side to lock in the maximum return.',
    learn: [
      {
        heading: 'How to use',
        body: "Find an opportunity and click Calculate. You can enter the total amount you want to stake across both bets and the tool will split it optimally, or you can enter a specific amount for one side and it will calculate how much you need on the other side to lock in the arbitrage.",
      },
      {
        heading: 'How arbitrage works',
        body: "Every pair of odds implies a probability. When two books price opposing sides of the same game, their combined implied probabilities normally exceed 100% — that's the house margin. Occasionally they diverge far enough that the combined total falls below 100%, meaning you can cover both sides for less than the guaranteed payout. That gap is the profit.",
      },
      {
        heading: 'Reading the results',
        body: "The profit percentage shown is the guaranteed return on your total stake — a 2% arb on $1,000 total staked returns roughly $20 no matter who wins. Stake amounts are pre-calculated and rounded to natural numbers so your bets don't look like a suspiciously precise pattern to the sportsbook. Because of that rounding, the two possible payouts won't be exactly equal, but they'll be very close. Place both bets as close to simultaneously as possible, since lines can move and close the window.",
      },
      {
        heading: 'Speed matters',
        body: "Arbitrage windows are short-lived. The moment one side moves, the opportunity closes. Have accounts funded and ready at multiple sportsbooks before you start. Place the less liquid side first — typically the smaller or less popular book — then immediately cover the other side.",
      },
      {
        heading: 'Account risk',
        body: "Arbitrage is the pattern sportsbooks are most aggressive about flagging. Accounts that consistently bet both sides across books will get limited. Occasionally place recreational bets at the same books, and treat each opportunity carefully. The math is guaranteed — protecting your access is what lets you keep doing it.",
      },
    ],
  },

  promo: {
    key: 'promo',
    icon: Gift,
    title: 'Promotion Converter',
    tagline: 'Turn promos into real cash',
    brief:
      "Sportsbook promotions — free bets, profit boosts, and deposit bonuses — can be converted into guaranteed cash. Enter a promo's details and the tool calculates the optimal hedge to lock in a guaranteed return. If you'd rather take on some risk for a higher potential payout, it also surfaces the highest positive-EV bet you can make with the promo given the adjusted odds.",
    learn: [
      {
        heading: 'Lock It In vs. Best Bet',
        body: "Every promo shows two panels. Lock It In finds the optimal hedge so you collect a guaranteed profit no matter which team wins — lower upside, zero risk. Best Bet surfaces the highest positive-EV bet for your promo, meaning the mathematically best bet given the boosted or free odds — higher potential upside, but the outcome isn't guaranteed. Use Lock It In to convert the promo into cash safely; use Best Bet if you're comfortable with variance and want to maximize expected return.",
      },
      { divider: 'Types of Promotions' },
      {
        heading: 'Free bet',
        body: "A free bet pays out profit only — if it wins, you keep the winnings but not the original stake. Example: a $50 free bet at +200 returns $100 profit if it wins. The conversion strategy is to place the free bet on a high-odds outcome and hedge the other side with real cash. Sized correctly, you pocket a guaranteed percentage of the free bet's face value as real cash no matter the outcome.",
      },
      {
        heading: 'No sweat / second chance bet',
        body: "A no sweat (or second chance) bet is a real-money bet with insurance — if it loses, your stake is refunded as a free bet. Example: place a $100 no sweat bet on a -110 spread. If it wins, you collect your normal payout. If it loses, you receive a $100 free bet. Either way, the tool helps you find the right hedge bet to place on the other side so that you lock in a guaranteed profit: if your no sweat bet wins, the hedge covers your loss on that side and you net a small profit; if it loses, you get the free bet back and convert that into guaranteed cash using the free bet method.",
      },
      {
        heading: 'Profit boost',
        body: "A profit boost increases your payout by a fixed percentage if your bet wins. Example: a 25% boost applied to a +100 bet effectively gives you +125 odds on that outcome. If the boosted line creates enough of a gap with the opposing side at another book, the tool finds an arb where both sides profit. Otherwise it identifies the highest-EV bet to apply the boost to.",
      },
      {
        heading: 'Deposit bonus',
        body: "A deposit bonus credits extra funds when you make a deposit, but requires a playthrough — meaning you must wager a certain total amount before any of it can be withdrawn. Example: a 100% match on a $1,000 deposit gives you $1,000 in bonus cash, but with a 1x playthrough you must wager $1,000 before withdrawing. The tool tells you how to bet through the playthrough requirement without losing money (or at worst a small loss), so that you can collect the full deposit bonus and walk away with a significant profit.",
      },
      {
        heading: 'Bet and get',
        body: "A bet and get requires placing a qualifying bet first, after which you receive a bonus bet. Example: 'Bet $50, Get $50' — place a $50 qualifying bet on any eligible game, then receive a $50 free bet once it settles. The tool tells you how to place the qualifying bet without losing money (or at worst a small loss), so that you unlock the bonus bet and convert it into a guaranteed profit using the free bet method.",
      },
      {
        heading: 'Account health',
        body: "Sportsbooks may limit or restrict accounts that consistently hedge or convert promotions. Scale your activity across multiple books, and avoid placing suspiciously precise hedge amounts every time. Keeping some recreational activity at each book helps protect your account access.",
      },
    ],
  },

  browse: {
    key: 'browse',
    icon: LayoutDashboard,
    title: 'Odds Dashboard',
    tagline: 'Every line, every book',
    brief:
      'Compare live odds from all major U.S. sportsbooks side-by-side for every game. The best available price for each outcome is highlighted automatically so you never leave money on the table by betting at the wrong book. Click any game to see alternate lines and player props. You can also add legs from any game to the Parlay Cart to see the combined payout and which sportsbook gives you the best odds for that specific parlay.',
    learn: [
      {
        heading: 'Reading the grid',
        body: "Each row is a game. Columns represent sportsbooks. The highlighted cell in each row is the best available price for that outcome. Odds that have moved since the last refresh are marked with an arrow showing the direction of the move.",
      },
      {
        heading: 'Market tabs',
        body: "Switch between moneyline (who wins outright), spread (margin of victory), and totals (combined score) using the tabs above the grid. Not every book offers every market on every game — gaps in the grid are normal.",
      },
      {
        heading: 'Game detail',
        body: "Click any game to open the full detail page. You'll see alternate spreads and totals at every half-point increment, all available player props with EV calculations, and a comparison against prediction market prices — which often reflect the sharpest available consensus on the true probability.",
      },
      {
        heading: 'Parlay cart',
        body: "Click any odds button to add a leg to the parlay cart on the right. The cart calculates the combined payout and shows which sportsbook gives the best overall parlay price, since each book's correlation rules and parlay boosts differ.",
      },
    ],
  },

  bets: {
    key: 'bets',
    icon: BookMarked,
    title: 'My Bets',
    tagline: 'Track your performance',
    brief:
      'Log every EV, arb, and promo bet you place and see how your results stack up over time. Your win rate, total P&L, and ROI update automatically as results come in — so you always know whether your strategy is actually working.',
    learn: [
      {
        heading: 'What it tracks',
        body: "You can track positive EV bets, arbitrage bets, and promo conversions. For each bet, the tracker stores the sportsbook, market, odds, stake, and expected value. As results come in, it records the actual outcome and calculates your realized profit or loss.",
      },
      {
        heading: 'How to log a bet',
        body: "Click 'Track Bet' on any EV or arb opportunity to pre-fill the details automatically, or add a bet manually from the Odds Dashboard. You'll enter the stake, odds, and bet type. Once logged, the bet sits as pending until the game settles.",
      },
      {
        heading: 'How results are resolved',
        body: "Pending bets are checked automatically against live scores every few minutes and settled as wins, losses, or pushes once the game is final. If a result doesn't resolve automatically, you can settle it manually from the bet log.",
      },
      {
        heading: 'Reading your stats',
        body: "The stats panel shows your total P&L, ROI (profit as a percentage of total amount wagered), win rate, and average edge per bet. These are broken down by bet type so you can see which strategy — EV, arb, or promo — is contributing most to your results.",
      },
      {
        heading: 'Why it matters',
        body: "The math behind +EV betting and arbitrage only plays out over a large enough sample. Short-term variance is real — you can do everything right and still have a losing stretch. Tracking your actual results gives you the data to distinguish bad luck from a flawed approach, and to see your edge compounding over time as the sample grows.",
      },
    ],
  },
};

function ToolLearnModal({ tool, onClose }) {
  const Icon = tool.icon;
  return (
    <div className="tool-learn-overlay" onClick={onClose}>
      <div className="tool-learn-modal" onClick={e => e.stopPropagation()}>
        <div className="tool-learn-header">
          <div className="tool-learn-header-left">
            <div className={`tool-learn-icon tool-intro-icon-${tool.key}`}>
              <Icon size={22} />
            </div>
            <div>
              <h2 className={`tool-learn-title tool-learn-title-${tool.key}`}>{tool.title}</h2>
              <p className="tool-learn-tagline">{tool.tagline}</p>
            </div>
          </div>
          <button className="tool-learn-close" onClick={onClose}>
            <X size={18} />
          </button>
        </div>
        <div className="tool-learn-body">
          <div className="tool-learn-note">
            Odds refresh periodically and may not match current sportsbook prices. Always verify before placing — live game lines in particular can shift significantly between refreshes.
          </div>
          {tool.learn.map(section => (
            section.divider ? (
              <div key={section.divider} className="tool-learn-divider">
                <span>{section.divider}</span>
              </div>
            ) : (
              <div key={section.heading} className="tool-learn-section">
                <h3 className={`tool-learn-section-heading tool-learn-section-heading-${tool.key}`}>
                  {section.heading}
                </h3>
                <p className="tool-learn-section-body">{section.body}</p>
              </div>
            )
          ))}
        </div>
      </div>
    </div>
  );
}

function ToolIntro({ toolKey, open, onClose }) {
  const [showModal, setShowModal] = useState(false);
  const tool = TOOLS[toolKey];

  React.useEffect(() => {
    if (open) setShowModal(true);
  }, [open]);

  if (!tool) return null;
  const Icon = tool.icon;

  const handleClose = () => {
    setShowModal(false);
    if (onClose) onClose();
  };

  return (
    <>
      <div className={`tool-intro tool-intro-${tool.key}`}>
        <div className="tool-intro-inner">
          <div className="tool-intro-left">
            <div className={`tool-intro-icon tool-intro-icon-${tool.key}`}>
              <Icon size={22} />
            </div>
            <div className="tool-intro-text">
              <div className="tool-intro-top-row">
                <h1 className={`tool-intro-title tool-intro-title-${tool.key}`}>{tool.title}</h1>
                <span className="tool-intro-tagline">{tool.tagline}</span>
              </div>
              <p className="tool-intro-brief">{tool.brief}</p>
            </div>
          </div>
          <button
            className={`tool-intro-learn-btn tool-intro-learn-btn-${tool.key}`}
            onClick={() => setShowModal(true)}
          >
            How it works →
          </button>
        </div>
      </div>
      {showModal && <ToolLearnModal tool={tool} onClose={handleClose} />}
    </>
  );
}

export default ToolIntro;
